import { Link } from "react-router-dom";
import { MatproLogo } from "@/components/MatproLogo";
import { Mail, MapPin, ShieldCheck, LifeBuoy } from "lucide-react";
import { useLang } from "@/context/LangContext";

export function Footer() {
  const year = new Date().getFullYear();
  const { t } = useLang();
  return (
    <footer className="border-t border-white/10 bg-matpro-anthracite text-zinc-300">
      <div className="matpro-container py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <div className="bg-white inline-flex rounded-md p-2 mb-4">
              <MatproLogo size="md" alwaysShowWordmark />
            </div>
            <p className="text-sm leading-relaxed text-zinc-400 max-w-xs">
              {t("footer.tagline")}
            </p>
            <div className="mt-4 text-[11px] tracking-[0.2em] uppercase font-bold text-matpro-green">
              {t("footer.buy_better_sell_more")}
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-400" data-testid="footer-belgium-tag">
              <span aria-hidden>🇧🇪</span>
              <span>{t("footer.made_in_be")}</span>
            </div>
          </div>

          <FooterColumn title={t("footer.col_marketplace")}>
            <FooterLink to="/recherche">{t("footer.link_search")}</FooterLink>
            <FooterLink to="/deposer">{t("footer.link_post_ad")}</FooterLink>
            <FooterLink to="/favoris">{t("footer.link_favorites")}</FooterLink>
            <FooterLink to="/entreprises-contactees">{t("footer.link_contacted")}</FooterLink>
            <FooterLink to="/messages">{t("footer.link_messages")}</FooterLink>
          </FooterColumn>

          <FooterColumn title={t("footer.col_matpass")}>
            <FooterLink to="/tarifs">{t("footer.link_pricing")}</FooterLink>
            <FooterLink to="/inscription?role=professionnel">{t("footer.link_become_pro")}</FooterLink>
            <FooterLink to="/pro">{t("footer.link_pro_area")}</FooterLink>
            <FooterLink to="/support">{t("footer.link_support")}</FooterLink>
          </FooterColumn>

          <FooterColumn title={t("footer.col_legal")}>
            <FooterLink to="/mentions-legales">{t("footer.link_legal_notices")}</FooterLink>
            <FooterLink to="/cgu">{t("footer.link_terms")}</FooterLink>
            <FooterLink to="/confidentialite">{t("footer.link_privacy")}</FooterLink>
            <FooterLink to="/support">{t("footer.link_contact")}</FooterLink>
          </FooterColumn>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center md:items-center justify-between gap-3 text-xs text-zinc-500 text-center md:text-left">
          <div>© {year} MATPASS · {t("footer.rights_reserved")}</div>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-matpro-green" /> {t("footer.stripe_secured")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-matpro-green" />
              <a href="mailto:contact@matpass.be" className="hover:text-white">contact@matpass.be</a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-white mb-4">{title}</div>
      <ul className="space-y-2 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  const isExternal = to.startsWith("mailto:") || to.startsWith("http");
  if (isExternal) {
    return <li><a href={to} className="text-zinc-400 hover:text-matpro-green transition-colors">{children}</a></li>;
  }
  return (
    <li>
      <Link to={to} className="text-zinc-400 hover:text-matpro-green transition-colors">{children}</Link>
    </li>
  );
}
